import { eur } from '../lib/format.ts'

export interface WeeklyBar {
  label: string
  cents: number
  baseCents: number
}

/**
 * Gráfico de barras de lo cobrado por semana.
 *
 * Escrito a mano en SVG en vez de con una librería de gráficas: Recharts añadía
 * unos 330 kB al paquete —más del doble de la app entera— por un único gráfico
 * de barras con una línea de referencia. En algo que se instala y actualiza por
 * datos móviles, eso no sale a cuenta.
 */
export function WeeklyBars({ data, height = 160 }: { data: WeeklyBar[]; height?: number }) {
  if (data.length === 0) return null

  const base = data[0].baseCents
  // La escala llega hasta el máximo real o la asignación, lo que sea mayor: así
  // una semana con bonus no se sale del marco y el listón siempre se ve.
  const top = Math.max(base, ...data.map((d) => d.cents)) * 1.1
  const gap = 6
  const width = 100
  const barWidth = (width - gap * (data.length - 1)) / data.length
  const baseY = height - (base / top) * height

  const colorOf = (cents: number) =>
    cents >= base
      ? 'var(--color-good)'
      : cents >= base * 0.6
        ? 'var(--color-warn)'
        : 'var(--color-bad)'

  return (
    <figure className="m-0">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="w-full"
        style={{ height }}
        role="img"
        aria-label={`Cobrado por semana: ${data.map((d) => `${d.label} ${eur(d.cents)}`).join(', ')}`}
      >
        {/* El listón de la asignación íntegra. */}
        <line
          x1={0}
          x2={width}
          y1={baseY}
          y2={baseY}
          stroke="var(--color-muted)"
          strokeWidth={0.5}
          strokeDasharray="2 2"
          vectorEffect="non-scaling-stroke"
        />
        {data.map((d, index) => {
          const barHeight = Math.max(1, (d.cents / top) * height)
          return (
            <rect
              key={index}
              x={index * (barWidth + gap)}
              y={height - barHeight}
              width={barWidth}
              height={barHeight}
              rx={1.5}
              fill={colorOf(d.cents)}
            />
          )
        })}
      </svg>

      <div className="mt-1 flex justify-between text-[10px] text-[var(--color-muted)]">
        {data.map((d, index) => (
          <span key={index} className="flex-1 text-center">
            {d.label}
          </span>
        ))}
      </div>
    </figure>
  )
}
